<template>
  <v-card elevation="0" class="mb-6">
    <v-card-title class="d-flex align-center justify-space-between px-0">
      <div class="d-flex align-center">
        <span class="font-weight-bold">{{ title }}</span>
      </div>
    </v-card-title>

    <v-card-text class="px-0">
      <div class="d-flex align-center flex-wrap" style="gap: 12px;">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          hide-details
          :placeholder="$t('search')"
          style="max-width: 280px;"
          clearable
          @input="debouncedFetch"
        />

        <v-autocomplete
          v-model="productFilter"
          :items="productOptions"
          item-text="text"
          item-value="val"
          outlined
          dense
          hide-details
          clearable
          :label="$t('product')"
          style="min-width: 200px;"
        />

        <v-autocomplete
          v-model="processorFilter"
          :items="processorOptions"
          item-text="text"
          item-value="val"
          outlined
          dense
          hide-details
          clearable
          :label="$t('processor')"
          style="min-width: 260px;"
        />

        <v-menu
          v-model="dateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="dateRangeText"
              prepend-inner-icon="mdi-calendar"
              outlined
              dense
              hide-details
              readonly
              :label="$t('dueDiligence.processingDate') || 'Processing Date'"
              style="min-width: 260px;"
              clearable
              @click:clear="clearDateRange"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="dateRange"
            range
            @change="dateMenu = false"
          />
        </v-menu>

        <v-spacer />

        <div class="d-flex align-center" style="gap: 8px;">
          <div class="text-body-2">
            {{ from }} - {{ to }} of {{ totalCount }}
          </div>
          <v-btn fab small outlined color="primary" :disabled="options.page <= 1" @click="pageChange(false)">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn
            fab
            small
            outlined
            color="primary"
            :disabled="options.page >= totalPages"
            @click="pageChange(true)"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>

      <v-data-table
        class="mt-4"
        :headers="headers"
        :items="pagedItems"
        :loading="loading"
        hide-default-footer
        :items-per-page="options.itemsPerPage"
      >
        <template v-slot:item.lotId="{ item }">
          <a href="#" @click.prevent="selectLot(item)">{{ item.lotId }}</a>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { debounce } from "lodash";
import CoffeeService from "@/_services/CoffeeService";
import CacaoDataService from "@/_services/CacaoDataService";

export default {
  name: "ProductLots",
  props: {
    sourceType: {
      type: String,
      required: true,
      validator: (v) => ["coffee", "cacao"].includes(v),
    },
    autoSelectFirst: {
      type: Boolean,
      default: true,
    },
    selectedLotId: {
      type: String,
      required: false,
      default: null,
    },
    productionPlaceLotInfo: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      search: "",
      productFilter: null,
      productOptions: [],
      processorFilter: null,
      processorOptions: [],
      dateMenu: false,
      dateRange: [],
      rawItems: [],
      totalCount: 0,
      from: 0,
      to: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
      },
    };
  },
  computed: {
    title() {
      return this.$t("Product Lots");
    },
    headers() {
      return [
        { text: this.$t("dueDiligence.lotId") || "Lot ID", value: "lotId", sortable: false },
        { text: this.$t("dueDiligence.productAndQuantity") || "Product & Quantity", value: "productQty", sortable: false },
        { text: this.$t("dueDiligence.qualityGade") || "Quality Grade", value: "qualityGrade", sortable: false },
        { text: this.$t("dueDiligence.buyingStationProcessor") || "Buying Station / Processor", value: "processor", sortable: false },
        { text: this.$t("dueDiligence.processingDate") || "Processing Date", value: "processingDate", sortable: false },
      ];
    },
    dateRangeText() {
      if (!this.dateRange || this.dateRange.length === 0) return "";
      if (this.dateRange.length === 1) return this.formatDate(this.dateRange[0]);
      if (this.dateRange.length === 2) {
        const start = this.formatDate(this.dateRange[0]);
        const end = this.formatDate(this.dateRange[1]);
        return `${start} - ${end}`;
      }
      return "";
    },
    filteredItems() {
      let items = this.rawItems;

      // Filter by processor
      if (this.processorFilter) {
        items = items.filter((x) => (x.processor || "").toLowerCase() === this.processorFilter.toLowerCase());
      }

      // Filter by product
      if (this.productFilter) {
        items = items.filter((x) => {
          const productName = this.extractProductName(x.original);
          return productName && productName.toLowerCase() === this.productFilter.toLowerCase();
        });
      }

      // Filter by date range
      if (this.dateRange && this.dateRange.length > 0) {
        items = items.filter((x) => {
          const processingDate = x.processingDate;
          if (!processingDate || processingDate === "N/A") return false;
          
          try {
            const itemDate = new Date(processingDate);
            if (isNaN(itemDate.getTime())) return false;

            if (this.dateRange.length === 1) {
              // Single date selected - match exact date
              const filterDate = new Date(this.dateRange[0]);
              return this.isSameDate(itemDate, filterDate);
            } else if (this.dateRange.length === 2) {
              // Date range selected
              const startDate = new Date(this.dateRange[0]);
              const endDate = new Date(this.dateRange[1]);
              startDate.setHours(0, 0, 0, 0);
              endDate.setHours(23, 59, 59, 999);
              itemDate.setHours(0, 0, 0, 0);
              return itemDate >= startDate && itemDate <= endDate;
            }
          } catch (e) {
            return false;
          }
          return false;
        });
      }

      return items;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredItems.length / this.options.itemsPerPage));
    },
    pagedItems() {
      const startIndex = (this.options.page - 1) * this.options.itemsPerPage;
      const endIndex = startIndex + this.options.itemsPerPage;
      return this.filteredItems.slice(startIndex, endIndex);
    },
  },
  watch: {
    sourceType: {
      immediate: true,
      handler() {
        this.options.page = 1;
        this.search = "";
        this.processorFilter = null;
        this.productFilter = null;
        this.dateRange = [];
        this.fetchLots();
      },
    },
    processorFilter() {
      this.options.page = 1;
      this.updatePager();
    },
    productFilter() {
      this.options.page = 1;
      this.updatePager();
    },
    dateRange() {
      this.options.page = 1;
      this.updatePager();
    },
    pagedItems() {
      this.updatePager();
    },
  },
  created() {
    this.debouncedFetch = debounce(() => {
      this.options.page = 1;
      this.fetchLots();
    }, 500);
  },
  methods: {
    selectLot(item) {
      this.$emit("select-lot", item.original);
    },
    pageChange(next) {
      this.options.page = next ? this.options.page + 1 : this.options.page - 1;
      this.updatePager();
    },
    updatePager() {
      this.totalCount = this.filteredItems.length;
      const start = (this.options.page - 1) * this.options.itemsPerPage;
      const end = start + this.pagedItems.length;
      this.from = this.totalCount === 0 ? 0 : start + 1;
      this.to = end;
      // Clamp page if filter reduced items.
      if (this.options.page > this.totalPages) this.options.page = this.totalPages;
    },
    extractProcessorName(item) {
      // Coffee: prefer dry milling / processor name when available; otherwise buying station.
      const tryPaths = [
        item?.processingBatch?.[0]?.dryMilling?.fullName,
        item?.parchmentCoffeeProcessingBatches?.[0]?.parchmentCoffee?.dryMilling?.fullName,
        item?.buyingStationOrder?.[0]?.buyingStation?.fullName,
        item?.cacaoPurchaseOrder?.[0]?.buyingStation?.fullName,
      ];
      return tryPaths.find(Boolean) || "N/A";
    },
    mapLotRow(item) {
      if (this.sourceType === "coffee") {
        const lotId = item.batchCode || "N/A";
        const qty = item.totalCoffeeCherryQty != null ? `${item.totalCoffeeCherryQty} KG` : "N/A";
        const quality = item.processingBatch?.[0]?.batchRating ?? "N/A";
        const processor = this.extractProcessorName(item);
        const date = item.endDate || "N/A";
        const productName = this.extractProductName({ ...item, type: "coffee" }) || "Coffee";
        return {
          lotId,
          productQty: `${productName}\n${qty}`,
          qualityGrade: quality,
          processor,
          processingDate: date,
          original: { ...item, type: "coffee" },
        };
      }

      // cacao
      const lotId = item.fermentationCode || "N/A";
      const qty = item.finalWeight != null ? `${item.finalWeight} KG` : "N/A";
      const processor = this.extractProcessorName(item);
      const date = item.endDate || "N/A";
      const productName = this.extractProductName({ ...item, type: "cacao" }) || "Cacao";
      return {
        lotId,
        productQty: `${productName}\n${qty}`,
        qualityGrade: "N/A",
        processor,
        processingDate: date,
        original: { ...item, type: "cacao" },
      };
    },
    extractProductName(originalItem) {
      if (!originalItem) return null;
      
      // For coffee, check various product fields
      if (this.sourceType === "coffee") {
        return (
          originalItem.productName ||
          originalItem.product?.name ||
          originalItem.processingBatch?.[0]?.productName ||
          "Coffee"
        );
      }
      
      // For cacao
      return (
        originalItem.productName ||
        originalItem.product?.name ||
        "Cacao"
      );
    },
    buildProcessorOptions() {
      const set = new Set();
      this.rawItems.forEach((x) => {
        if (x.processor && x.processor !== "N/A") set.add(x.processor);
      });
      this.processorOptions = Array.from(set)
        .sort((a, b) => a.localeCompare(b))
        .map((p) => ({ text: p, val: p }));
    },
    buildProductOptions() {
      const set = new Set();
      this.rawItems.forEach((x) => {
        const productName = this.extractProductName(x.original);
        if (productName && productName !== "N/A") set.add(productName);
      });
      this.productOptions = Array.from(set)
        .sort((a, b) => a.localeCompare(b))
        .map((p) => ({ text: p, val: p }));
    },
    formatDate(dateString) {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      } catch (e) {
        return dateString;
      }
    },
    isSameDate(date1, date2) {
      return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
      );
    },
    clearDateRange() {
      this.dateRange = [];
      this.dateMenu = false;
    },
    lotMatchesProductionPlace(lot) {
      // Check if this lot matches any production place lot information
      if (!this.productionPlaceLotInfo || this.productionPlaceLotInfo.length === 0) {
        return true; // If no filter, show all lots
      }

      const lotId = this.sourceType === "coffee" ? lot.batchCode : lot.fermentationCode;
      const lotItemId = lot.id;

      // Match by sourceType, lotId (batchCode/fermentationCode), and lotItemId
      return this.productionPlaceLotInfo.some(ppLot =>
        ppLot.sourceType === this.sourceType &&
        ppLot.lotId === lotId &&
        ppLot.lotItemId === Number(lotItemId)
      );
    },
    async fetchLots() {
      this.loading = true;
      try {
        const options = { page: 1, itemsPerPage: 500 };
        const serviceRes =
          this.sourceType === "coffee"
            ? await CoffeeService.getCoffeeData(options, this.search)
            : await CacaoDataService.getCacaoData(options, this.search);

        const ok = serviceRes?.code === 200;
        const response = ok ? serviceRes?.data?.response : [];
        // const count = ok ? serviceRes?.data?.count : 0;

        // The backend often returns the full list; we keep local paging for consistent UX.
        const list = Array.isArray(response) ? response : [];
        
        // Filter lots to only include those related to production places
        const filteredList = this.productionPlaceLotInfo && this.productionPlaceLotInfo.length > 0
          ? list.filter(lot => this.lotMatchesProductionPlace(lot))
          : list;
        
        this.rawItems = filteredList.map((i) => this.mapLotRow(i));
        this.totalCount = this.rawItems.length;
        this.buildProcessorOptions();
        this.buildProductOptions();
        this.updatePager();

        // Auto-select first lot once we have data, but don't override an existing selection.
        if (this.autoSelectFirst && !this.selectedLotId && this.rawItems.length > 0) {
          this.$emit("select-lot", this.rawItems[0].original);
        }
      } catch (e) {
        // Fail soft; keep table empty.
        this.rawItems = [];
        this.processorOptions = [];
        this.productOptions = [];
        this.totalCount = 0;
        this.updatePager();
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Keep product + qty readable in table cell */
::v-deep td {
  white-space: pre-line;
}
</style>


